import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import { useClearedQueryParamValue } from 'src/app/shared/hooks/location';
import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { PetitionCommentsPageCombinedState, PetitionCommentsPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PetitionCommentsPageDataContextProvider } from './pageData';
import { PetitionCommentsPagePaginatedDataContextProvider } from './pagePaginatedData';
import { PetitionCommentsPageStateContextProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<PetitionCommentsPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PetitionCommentsPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function PetitionCommentsPageContextProvider({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData);
	const deleteConfirmMessage = useClearedQueryParamValue('deleted_comment') === 'true';
	const userId = useCurrentUserId();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pagePaginatedData } = pageContextState;

	return (
		<PetitionCommentsPageDataContextProvider pageData={pageData}>
			<PetitionCommentsPagePaginatedDataContextProvider pagePaginatedData={pagePaginatedData}>
				<PetitionCommentsPageStateContextProvider deleteConfirmMessage={deleteConfirmMessage}>
					<TrackingContext
						properties={{
							petition_id: pageData.data.petition.id,
							own_petition: pageData.data.petition.user.id === userId,
						}}
					>
						{children}
					</TrackingContext>
				</PetitionCommentsPageStateContextProvider>
			</PetitionCommentsPagePaginatedDataContextProvider>
		</PetitionCommentsPageDataContextProvider>
	);
}
